'use client';

import type { YoutubeEmbedBlock } from '@jeraisonne/ai/visual-blocks';

/**
 * Extrait l'identifiant vidéo d'une URL YouTube dans tous les formats courants :
 * - youtube.com/watch?v=ID
 * - youtu.be/ID
 * - youtube.com/embed/ID
 * - youtube.com/shorts/ID
 * - youtube.com/live/ID
 *
 * Retourne null si rien d'exploitable. Tolère URLs avec ou sans protocole,
 * et garde les paramètres query (timestamp, etc.) côté caller.
 */
export function extractYoutubeVideoId(url: string): string | null {
  if (!url) return null;
  const trimmed = url.trim();

  // Forme directe : 11 caractères alphanum/-_
  if (/^[A-Za-z0-9_-]{11}$/.test(trimmed)) return trimmed;

  // Patterns d'URL YouTube
  const patterns = [
    /[?&]v=([A-Za-z0-9_-]{11})/, // watch?v=ID
    /youtu\.be\/([A-Za-z0-9_-]{11})/, // youtu.be/ID
    /\/embed\/([A-Za-z0-9_-]{11})/, // /embed/ID
    /\/shorts\/([A-Za-z0-9_-]{11})/, // /shorts/ID
    /\/live\/([A-Za-z0-9_-]{11})/, // /live/ID
  ];

  for (const re of patterns) {
    const m = re.exec(trimmed);
    if (m?.[1]) return m[1];
  }
  return null;
}

/**
 * Extrait le timestamp de démarrage (en secondes) d'une URL YouTube.
 * Accepte `t=90`, `t=1m30s`, `start=90`.
 */
export function extractYoutubeStart(url: string): number | undefined {
  const m = /[?&](?:t|start)=([^&#]+)/.exec(url);
  if (!m?.[1]) return undefined;
  const raw = m[1];
  if (/^\d+$/.test(raw)) return Number(raw);
  // Format 1h2m3s / 2m30s / 45s
  let total = 0;
  const re = /(\d+)([hms])/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(raw)) !== null) {
    const n = Number(match[1]);
    if (match[2] === 'h') total += n * 3600;
    else if (match[2] === 'm') total += n * 60;
    else total += n;
  }
  return total > 0 ? total : undefined;
}

export function YoutubeEmbedBlockRenderer({ block }: { block: YoutubeEmbedBlock }) {
  // Si l'id est invalide pour une raison X, on fallback sur un lien.
  if (!block.video_id) {
    return (
      <a
        href={block.url}
        target="_blank"
        rel="noreferrer"
        className="text-sm text-blue-600 underline"
      >
        Voir la vidéo YouTube
      </a>
    );
  }
  const params = new URLSearchParams({
    rel: '0',
    modestbranding: '1',
  });
  if (block.start && block.start > 0) params.set('start', String(block.start));
  const embedUrl = `https://www.youtube-nocookie.com/embed/${block.video_id}?${params.toString()}`;

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-black">
      <iframe
        src={embedUrl}
        title={block.title ?? 'Vidéo YouTube'}
        className="absolute inset-0 h-full w-full"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}
